import React, { useEffect, useMemo, useState } from "react";
import {
    Trophy,
    Award,
    Code,
    Brain,
    BarChart3,
    Shield,
    Cloud,
    Smartphone,
    GitBranch,
    Palette,
    Gamepad2,
    Link,
    Database,
    Coffee,
    FileCode,
    Layout,
    Server,
    Workflow,
    Cpu,
    Globe,
    Download,
    CheckCircle,
    Medal,
    ExternalLink,
    ChevronDown,
    ChevronUp,
    Loader,
    AlertCircle,
    Rocket,
    Target,
    Zap,
    TrendingUp,
    Star,
    Gift
} from "lucide-react";

import httpClient from "../Api/axios";

const CertificationPage = ({ userId }) => {

    const [loading, setLoading] = useState(true);
    const [expandedDomains, setExpandedDomains] = useState({});
    const [certificatesFromBackend, setCertificatesFromBackend] = useState([]);
    const [domainGroups, setDomainGroups] = useState({});
    const [domainCertificates, setDomainCertificates] = useState([]);
    const [downloading, setDownloading] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);
    const [error, setError] = useState("");
    const [userName, setUserName] = useState("");
    const [generatingDomain, setGeneratingDomain] = useState(null);
    const [showSuccess, setShowSuccess] = useState(null);
    const [syncingTopics, setSyncingTopics] = useState(false);

    // ================= DOMAIN CONFIG =================

    const domainConfig = useMemo(() => ({
        "Full Stack Development": {
            icon: <Code size={48} />,
            color: "#5a54eb",
            bgGradient: "linear-gradient(135deg, #5a54eb, #8b5cf6)",
            lightColor: "rgba(90, 84, 235, 0.1)",
            topics: [
                "C Programming",
                "Java",
                "HTML",
                "CSS",
                "JavaScript",
                "JSP",
                "Servlet",
                "ReactJS",
                "Database (MySQL)",
                "CRUD APIs",
                "SpringBoot"
            ]
        },
        "AIML": {
            icon: <Brain size={48} />,
            color: "#10b981",
            bgGradient: "linear-gradient(135deg, #10b981, #34d399)",
            lightColor: "rgba(16, 185, 129, 0.1)",
            topics: [
                "Python",
                "NumPy, Pandas",
                "Machine Learning (Scikit-learn)",
                "Deep Learning (TensorFlow / PyTorch)",
                "Data Visualization (Matplotlib, Seaborn)",
                "NLP basics"
            ]
        },
        "Data Analytics / Data Science": {
            icon: <BarChart3 size={48} />,
            color: "#f59e0b",
            bgGradient: "linear-gradient(135deg, #f59e0b, #fbbf24)",
            lightColor: "rgba(245, 158, 11, 0.1)",
            topics: [
                "Python / R",
                "Excel Advanced",
                "SQL",
                "Power BI / Tableau",
                "Data Cleaning",
                "Statistics basics"
            ]
        },
        "Cybersecurity": {
            icon: <Shield size={48} />,
            color: "#ef4444",
            bgGradient: "linear-gradient(135deg, #ef4444, #f87171)",
            lightColor: "rgba(239, 68, 68, 0.1)",
            topics: [
                "Networking Basics",
                "Linux",
                "Ethical Hacking",
                "Cryptography basics",
                "Web Security (OWASP)",
                "Tools (Wireshark, Metasploit)"
            ]
        },
        "Cloud Computing": {
            icon: <Cloud size={48} />,
            color: "#3b82f6",
            bgGradient: "linear-gradient(135deg, #3b82f6, #60a5fa)",
            lightColor: "rgba(59, 130, 246, 0.1)",
            topics: [
                "Cloud Basics",
                "AWS / Azure / GCP",
                "Docker",
                "Kubernetes",
                "CI/CD",
                "Serverless computing"
            ]
        },
        "Mobile App Development": {
            icon: <Smartphone size={48} />,
            color: "#8b5cf6",
            bgGradient: "linear-gradient(135deg, #8b5cf6, #a78bfa)",
            lightColor: "rgba(139, 92, 246, 0.1)",
            topics: [
                "Java / Kotlin (Android)",
                "Flutter / Dart",
                "React Native",
                "API Integration",
                "UI/UX basics"
            ]
        },
        "DevOps Engineering": {
            icon: <GitBranch size={48} />,
            color: "#06b6d4",
            bgGradient: "linear-gradient(135deg, #06b6d4, #22d3ee)",
            lightColor: "rgba(6, 182, 212, 0.1)",
            topics: [
                "Linux",
                "Git & GitHub",
                "Jenkins",
                "Docker",
                "Kubernetes",
                "CI/CD pipelines"
            ]
        },
        "UIUX": {
            icon: <Palette size={48} />,
            color: "#ec4899",
            bgGradient: "linear-gradient(135deg, #ec4899, #f472b6)",
            lightColor: "rgba(236, 72, 153, 0.1)",
            topics: [
                "Figma / Adobe XD",
                "Wireframing",
                "Prototyping",
                "User Research",
                "Design Principles"
            ]
        },
        "Game Development": {
            icon: <Gamepad2 size={48} />,
            color: "#f43f5e",
            bgGradient: "linear-gradient(135deg, #f43f5e, #fb7185)",
            lightColor: "rgba(244, 63, 94, 0.1)",
            topics: [
                "C++",
                "C#",
                "Unity / Unreal Engine",
                "Game Physics",
                "Animation basics"
            ]
        },
        "Blockchain Development": {
            icon: <Link size={48} />,
            color: "#14b8a6",
            bgGradient: "linear-gradient(135deg, #14b8a6, #2dd4bf)",
            lightColor: "rgba(20, 184, 166, 0.1)",
            topics: [
                "Blockchain Basics",
                "Solidity",
                "Smart Contracts",
                "Ethereum",
                "Web3.js"
            ]
        }
    }), []);

    // ================= TOPIC ICON MAP =================

    const topicIconMap = {
        "C Programming": <Cpu size={26} />,
        "Java": <Coffee size={26} />,
        "HTML": <Layout size={26} />,
        "CSS": <Layout size={26} />,
        "JavaScript": <Code size={26} />,
        "JSP": <Server size={26} />,
        "Servlet": <Server size={26} />,
        "ReactJS": <Code size={26} />,
        "Database (MySQL)": <Database size={26} />,
        "CRUD APIs": <Workflow size={26} />,
        "SpringBoot": <Coffee size={26} />,
        "Python": <FileCode size={26} />,
        "NumPy, Pandas": <BarChart3 size={26} />,
        "Machine Learning (Scikit-learn)": <Brain size={26} />,
        "Deep Learning (TensorFlow / PyTorch)": <Brain size={26} />,
        "Data Visualization (Matplotlib, Seaborn)": <BarChart3 size={26} />,
        "NLP basics": <Globe size={26} />
    };

    // ================= HELPERS =================

    const normalizeText = (text) => {
        return (text || "").trim().toLowerCase();
    };

    const safeDate = (date) => {
        try {
            if (!date) return new Date().toLocaleDateString();
            return new Date(date).toLocaleDateString();
        } catch {
            return new Date().toLocaleDateString();
        }
    };

    // ================= DOWNLOAD IMAGE =================

    const downloadImage = async (imageUrl, fileName) => {
        try {
            const response = await fetch(imageUrl);
            if (!response.ok) {
                throw new Error("Failed to fetch certificate");
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error(error);
            alert("Failed to download certificate");
        }
    };

    // ================= DOWNLOAD CERTIFICATE =================

    const handleDownloadCertificate = async (
        certificateUrl,
        fileName,
        certificateId
    ) => {
        if (!certificateUrl) {
            alert("Certificate URL not found");
            return;
        }
        try {
            setDownloading(true);
            setDownloadingId(certificateId || fileName);
            let finalFileName = fileName;
            if (!finalFileName.endsWith(".png") && !finalFileName.endsWith(".jpg") && !finalFileName.endsWith(".jpeg")) {
                finalFileName += ".png";
            }
            await downloadImage(certificateUrl, finalFileName);
        } catch (error) {
            console.error(error);
        } finally {
            setDownloading(false);
            setDownloadingId(null);
        }
    };

    // ================= SYNC COMPLETED TOPICS TO BACKEND =================
    
    const syncCompletedTopicsToBackend = async (currentUserId, completedQuizzes) => {
        if (!currentUserId || syncingTopics) return;
        
        setSyncingTopics(true);
        
        try {
            // Group completed topics by domain
            const topicsByDomain = {};
            
            Object.entries(completedQuizzes).forEach(([topic, completed]) => {
                if (!completed) return;
                
                for (const [domain, config] of Object.entries(domainConfig)) {
                    if (config.topics.includes(topic)) {
                        if (!topicsByDomain[domain]) topicsByDomain[domain] = [];
                        topicsByDomain[domain].push(topic);
                        break;
                    }
                }
            });
            
            // For each domain, sync the completed topics
            for (const [domain, topics] of Object.entries(topicsByDomain)) {
                try {
                    await httpClient.post("/payment/sync-completed-topics", {
                        userId: currentUserId,
                        completedTopics: topics,
                        domain: domain,
                        userName: userName
                    });
                    console.log(`✅ Synced ${topics.length} topics for ${domain}`);
                } catch (err) {
                    console.error(`Error syncing topics for ${domain}:`, err);
                }
            }
        } catch (error) {
            console.error("Error in syncCompletedTopicsToBackend:", error);
        } finally {
            setSyncingTopics(false);
        }
    };

    // ================= CHECK AND GENERATE DOMAIN CERTIFICATE =================

    const checkAndGenerateDomainCertificates = async () => {
        const currentUserId = userId || localStorage.getItem("userId");
        const currentUserName = userName || localStorage.getItem("userName") || "User";
        
        if (!currentUserId) return;
        
        try {
            setGeneratingDomain("checking");
            const response = await httpClient.post("/payment/check-domain-certificate", {
                userId: currentUserId,
                userName: currentUserName
            });
            
            console.log("Domain certificate response:", response.data);
            
            if (response.data.generated && response.data.certificates && response.data.certificates.length > 0) {
                const domainNames = response.data.certificates.map(c => c.domainName).join(", ");
                setShowSuccess(domainNames);
                
                setTimeout(() => {
                    setShowSuccess(null);
                    window.location.reload();
                }, 3000);
            }
        } catch (error) {
            console.error("Error checking domain certificates:", error);
        } finally {
            setGeneratingDomain(null);
        }
    };

    // ================= FETCH DATA =================

    useEffect(() => {
        const fetchCertificates = async () => {
            try {
                setLoading(true);
                const currentUserId = userId || localStorage.getItem("userId");
                const storedUserName = localStorage.getItem("userName") || "User";
                setUserName(storedUserName);

                if (!currentUserId) {
                    setLoading(false);
                    return;
                }

                // ================= LOCAL STORAGE DATA (Load First) =================
                const completedQuizzes = JSON.parse(
                    localStorage.getItem(`completedQuizzes_${currentUserId}`)
                ) || {};

                const localDomainCerts = JSON.parse(
                    localStorage.getItem(`domainCertificates_${currentUserId}`)
                ) || {};

                // ================= SYNC LOCAL COMPLETED QUIZZES TO BACKEND =================
                await syncCompletedTopicsToBackend(currentUserId, completedQuizzes);

                // ================= FETCH FROM BACKEND =================
                let backendCerts = [];
                let domainCertsFromBackend = [];
                
                try {
                    const response = await httpClient.post("/payment/certificate", {
                        userId: currentUserId
                    });
                    console.log("Certificate API Response:", response.data);
                    
                    if (response.data && response.data.certificates && Array.isArray(response.data.certificates)) {
                        backendCerts = response.data.certificates;
                        
                        domainCertsFromBackend = backendCerts.filter(cert => cert.certificateType === "DOMAIN");
                        backendCerts = backendCerts.filter(cert => cert.certificateType !== "DOMAIN");
                    }
                } catch (apiError) {
                    console.error("Backend API error:", apiError);
                }

                // ===== REMOVE DUPLICATES BY CERTIFICATE URL =====
                const uniqueCertificatesMap = new Map();
                
                backendCerts.forEach((cert) => {
                    const uniqueKey = cert.certificateUrl || cert.certificateId;
                    if (!uniqueCertificatesMap.has(uniqueKey)) {
                        uniqueCertificatesMap.set(uniqueKey, {
                            ...cert,
                            displayTitle: cert.quizTitle || "Certificate",
                            uniqueId: uniqueKey
                        });
                    }
                });
                
                const uniqueCertificates = Array.from(uniqueCertificatesMap.values());
                setCertificatesFromBackend(uniqueCertificates);

                // ================= CREATE DOMAIN GROUPS =================
                const groups = {};
                Object.entries(domainConfig).forEach(([domain, config]) => {
                    groups[domain] = {
                        name: domain,
                        icon: config.icon,
                        color: config.color,
                        bgGradient: config.bgGradient,
                        lightColor: config.lightColor,
                        totalTopics: config.topics.length,
                        completedTopics: [],
                        domainCertificateEarned: localDomainCerts[domain] || 
                            domainCertsFromBackend.some(cert => cert.domainName === domain),
                        domainCertificateDate: new Date().toLocaleDateString(),
                        domainCertificateUrl: domainCertsFromBackend.find(cert => cert.domainName === domain)?.certificateUrl || null
                    };
                });

                // ================= CREATE CERTIFICATE LOOKUP MAP =================
                const certificateMap = new Map();
                uniqueCertificates.forEach((cert) => {
                    certificateMap.set(cert.certificateUrl, cert);
                });

                // ================= PROCESS COMPLETED TOPICS =================
                Object.entries(completedQuizzes).forEach(([topic, completed]) => {
                    if (!completed) return;

                    Object.entries(domainConfig).forEach(([domain, config]) => {
                        if (config.topics.includes(topic)) {
                            let matchedCert = null;
                            for (let [url, cert] of certificateMap.entries()) {
                                if (url && url.toLowerCase().includes(topic.toLowerCase())) {
                                    matchedCert = cert;
                                    break;
                                }
                            }
                            
                            groups[domain].completedTopics.push({
                                name: topic,
                                icon: topicIconMap[topic] || <Award size={26} />,
                                date: new Date().toLocaleDateString(),
                                hasRealCertificate: !!matchedCert,
                                certificateUrl: matchedCert?.certificateUrl || "",
                                certificateId: matchedCert?.certificateId || "",
                                quizTitle: matchedCert?.quizTitle || topic
                            });
                        }
                    });
                });

                // ================= PROCESS DOMAIN CERTIFICATES FROM BACKEND =================
                const domains = [];
                domainCertsFromBackend.forEach((cert) => {
                    if (cert.domainName && domainConfig[cert.domainName]) {
                        domains.push({
                            name: cert.domainName,
                            icon: domainConfig[cert.domainName].icon,
                            color: domainConfig[cert.domainName].color,
                            bgGradient: domainConfig[cert.domainName].bgGradient,
                            date: safeDate(cert.issuedDate),
                            certificateUrl: cert.certificateUrl,
                            certificateId: cert.certificateId
                        });
                    }
                });

                Object.entries(localDomainCerts).forEach(([domainName, earned]) => {
                    if (earned && domainConfig[domainName] && !domains.some(d => d.name === domainName)) {
                        domains.push({
                            name: domainName,
                            icon: domainConfig[domainName].icon,
                            color: domainConfig[domainName].color,
                            bgGradient: domainConfig[domainName].bgGradient,
                            date: new Date().toLocaleDateString()
                        });
                    }
                });

                setDomainGroups(groups);
                setDomainCertificates(domains);

                // ================= INITIALIZE EXPANDED STATE =================
                const expanded = {};
                Object.keys(groups).forEach((domain) => {
                    expanded[domain] = true;
                });
                setExpandedDomains(expanded);

                // ================= CHECK FOR NEW DOMAIN CERTIFICATES =================
                await checkAndGenerateDomainCertificates();

            } catch (error) {
                console.error("Error fetching certificates:", error);
                setError(error?.response?.data?.message || "Failed to load certificates");
            } finally {
                setLoading(false);
            }
        };

        fetchCertificates();
    }, [userId]);

    // ================= TOGGLE DOMAIN =================
    const toggleDomain = (domainName) => {
        setExpandedDomains((prev) => ({
            ...prev,
            [domainName]: !prev[domainName]
        }));
    };

    // ================= MANUAL CLAIM DOMAIN CERTIFICATE =================
    const handleClaimDomainCertificate = async (domainName) => {
        const currentUserId = userId || localStorage.getItem("userId");
        const currentUserName = userName || localStorage.getItem("userName") || "User";
        
        try {
            setGeneratingDomain(domainName);
            
            // First sync completed topics to backend
            const completedQuizzes = JSON.parse(
                localStorage.getItem(`completedQuizzes_${currentUserId}`)
            ) || {};
            
            await syncCompletedTopicsToBackend(currentUserId, completedQuizzes);
            
            // Then check for domain certificate
            const response = await httpClient.post("/payment/check-domain-certificate", {
                userId: currentUserId,
                userName: currentUserName
            });
            
            if (response.data.generated && response.data.certificates) {
                const claimed = response.data.certificates.find(c => c.domainName === domainName);
                if (claimed) {
                    setShowSuccess(domainName);
                    setTimeout(() => {
                        setShowSuccess(null);
                        window.location.reload();
                    }, 3000);
                } else {
                    alert("No certificate available for this domain yet. Complete all topics first!");
                }
            } else {
                alert("No certificate available for this domain yet. Complete all topics first!");
            }
        } catch (error) {
            console.error("Error claiming domain certificate:", error);
            alert("Failed to generate certificate. Please try again.");
        } finally {
            setGeneratingDomain(null);
        }
    };

    // ================= COUNTS =================
    const totalCertificates = certificatesFromBackend.length + domainCertificates.length;
    const totalTopicsCompleted = Object.values(domainGroups).reduce(
        (total, domain) => total + domain.completedTopics.length,
        0
    );

    // ================= GET MOTIVATIONAL MESSAGE =================
    const getMotivationalMessage = (domain) => {
        const completed = domain.completedTopics.length;
        const total = domain.totalTopics;
        const percentage = (completed / total) * 100;
        
        if (domain.domainCertificateEarned) {
            return {
                message: `🎉 Congratulations! You've mastered ${domain.name}!`,
                icon: <Trophy size={20} />,
                color: "#fbbf24",
                showProgress: false,
                showClaimButton: false
            };
        } else if (percentage === 100) {
            return {
                message: `🎯 You've completed all topics! Click "Claim Certificate" to get your mastery certificate!`,
                icon: <Gift size={20} />,
                color: "#f59e0b",
                showProgress: true,
                showClaimButton: true
            };
        } else if (percentage === 0) {
            return {
                message: `Start your journey in ${domain.name} by completing your first quiz!`,
                icon: <Rocket size={20} />,
                color: "#60a5fa",
                showProgress: true,
                showClaimButton: false
            };
        } else if (percentage < 50) {
            const remaining = total - completed;
            return {
                message: `💪 Great start! Complete ${remaining} more topic${remaining > 1 ? 's' : ''} to unlock your certificate!`,
                icon: <Zap size={20} />,
                color: "#fbbf24",
                showProgress: true,
                showClaimButton: false
            };
        } else {
            const remaining = total - completed;
            return {
                message: `🌟 Almost there! Just ${remaining} more topic${remaining > 1 ? 's' : ''} to become a ${domain.name} expert!`,
                icon: <Target size={20} />,
                color: "#34d399",
                showProgress: true,
                showClaimButton: false
            };
        }
    };

    // ================= LOADING =================
    if (loading) {
        return (
            <div style={{
                minHeight: "100vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                background: "linear-gradient(135deg, #0f0f13 0%, #1a1a1d 100%)"
            }}>
                <div style={{ textAlign: "center" }}>
                    <Loader size={52} color="#5a54eb" style={{ animation: "spin 1s linear infinite" }} />
                    <style>{`
                        @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                        }
                    `}</style>
                    <p style={{ color: "#a5a6ad", marginTop: "1rem" }}>Loading Certificates...</p>
                </div>
            </div>
        );
    }

    // ================= UI =================
    return (
        <div className="certification-container">
            <style>{`
                * {
                    box-sizing: border-box;
                    margin: 0;
                    padding: 0;
                }
                
                .certification-container {
                    width: 100%;
                    min-height: 100vh;
                    padding: clamp(1rem, 5vw, 3rem);
                    background: linear-gradient(135deg, #0f0f13 0%, #1a1a1d 100%);
                }
                
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                @keyframes slideIn {
                    from {
                        transform: translateX(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
                
                .cert-header {
                    text-align: center;
                    margin-bottom: 3rem;
                    animation: fadeInUp 0.6s ease-out;
                }
                
                .cert-header h1 {
                    font-size: clamp(1.8rem, 5vw, 3rem);
                    margin-bottom: 0.5rem;
                    background: linear-gradient(135deg, #5a54eb, #8b5cf6, #a78bfa);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                }
                
                .cert-header p {
                    color: #a5a6ad;
                    font-size: clamp(0.9rem, 3vw, 1rem);
                }
                
                .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 1.5rem;
                    margin-bottom: 3rem;
                    animation: fadeInUp 0.6s ease-out 0.1s both;
                }
                
                .stat-card {
                    background: rgba(35, 35, 40, 0.8);
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 20px;
                    padding: clamp(1rem, 4vw, 1.8rem);
                    text-align: center;
                    transition: all 0.3s ease;
                    cursor: pointer;
                }
                
                .stat-card:hover {
                    transform: translateY(-5px);
                    border-color: #5a54eb;
                    box-shadow: 0 10px 30px rgba(90, 84, 235, 0.2);
                }
                
                .stat-value {
                    font-size: clamp(1.8rem, 5vw, 2.5rem);
                    font-weight: 700;
                    background: linear-gradient(135deg, #5a54eb, #8b5cf6);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }
                
                .stat-label {
                    margin-top: 0.5rem;
                    color: #bbb;
                    font-size: clamp(0.8rem, 3vw, 0.9rem);
                }
                
                .section-title {
                    display: flex;
                    align-items: center;
                    gap: 0.8rem;
                    color: white;
                    margin-bottom: 1.5rem;
                    margin-top: 2rem;
                    font-size: clamp(1.1rem, 4vw, 1.3rem);
                    animation: fadeInUp 0.6s ease-out;
                }
                
                .badge {
                    background: linear-gradient(135deg, #5a54eb, #8b5cf6);
                    padding: 0.25rem 0.75rem;
                    border-radius: 30px;
                    font-size: 0.8rem;
                }
                
                .certificates-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
                    gap: 1.5rem;
                    animation: fadeInUp 0.6s ease-out 0.2s both;
                }
                
                .certificate-card {
                    background: rgba(35, 35, 40, 0.8);
                    backdrop-filter: blur(10px);
                    border-radius: 22px;
                    overflow: hidden;
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    transition: all 0.3s ease;
                }
                
                .certificate-card:hover {
                    transform: translateY(-8px);
                    border-color: #5a54eb;
                    box-shadow: 0 15px 40px rgba(90, 84, 235, 0.25);
                }
                
                .certificate-header {
                    padding: clamp(1.5rem, 4vw, 2rem);
                    text-align: center;
                    background: linear-gradient(135deg, #5a54eb, #8b5cf6);
                    color: white;
                }
                
                .certificate-body {
                    padding: clamp(1rem, 4vw, 1.5rem);
                    text-align: center;
                }
                
                .certificate-body h3 {
                    color: white;
                    margin-bottom: 0.5rem;
                    font-size: clamp(1rem, 3vw, 1.2rem);
                }
                
                .certificate-date {
                    color: #aaa;
                    font-size: 0.8rem;
                    margin-bottom: 1rem;
                }
                
                .certificate-name {
                    display: inline-block;
                    padding: 0.4rem 1rem;
                    border-radius: 30px;
                    background: rgba(90, 84, 235, 0.15);
                    color: #8b5cf6;
                    font-size: 0.75rem;
                    margin-bottom: 1rem;
                }
                
                .certificate-actions {
                    display: flex;
                    gap: 0.8rem;
                    justify-content: center;
                    flex-wrap: wrap;
                }
                
                .cert-btn {
                    border: none;
                    padding: 0.7rem 1.2rem;
                    border-radius: 12px;
                    cursor: pointer;
                    font-weight: 600;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    transition: all 0.3s ease;
                    font-size: 0.85rem;
                }
                
                .cert-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }
                
                .download-btn {
                    background: linear-gradient(135deg, #5a54eb, #8b5cf6);
                    color: white;
                }
                
                .download-btn:hover:not(:disabled) {
                    transform: scale(1.05);
                    box-shadow: 0 5px 15px rgba(90, 84, 235, 0.4);
                }
                
                .view-btn {
                    background: rgba(51, 51, 51, 0.8);
                    color: white;
                    border: 1px solid rgba(255, 255, 255, 0.1);
                }
                
                .view-btn:hover:not(:disabled) {
                    background: rgba(68, 68, 68, 0.9);
                    transform: scale(1.05);
                }
                
                .claim-btn {
                    background: linear-gradient(135deg, #f59e0b, #fbbf24);
                    color: #1a1a1d;
                    border: none;
                    padding: 0.5rem 1rem;
                    border-radius: 20px;
                    cursor: pointer;
                    font-weight: 600;
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-size: 0.75rem;
                    transition: all 0.3s ease;
                }
                
                .claim-btn:hover:not(:disabled) {
                    transform: scale(1.05);
                    box-shadow: 0 2px 8px rgba(245, 158, 11, 0.4);
                }
                
                .domain-section {
                    margin-top: 2rem;
                    border-radius: 20px;
                    overflow: hidden;
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    background: rgba(35, 35, 40, 0.6);
                    backdrop-filter: blur(10px);
                    transition: all 0.3s ease;
                    animation: fadeInUp 0.6s ease-out;
                }
                
                .domain-section:hover {
                    border-color: rgba(90, 84, 235, 0.5);
                }
                
                .domain-header {
                    padding: clamp(1rem, 3vw, 1.5rem);
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    cursor: pointer;
                    transition: background 0.3s ease;
                }
                
                .domain-header:hover {
                    background: rgba(255, 255, 255, 0.05);
                }
                
                .domain-info {
                    display: flex;
                    gap: clamp(0.8rem, 3vw, 1.5rem);
                    align-items: center;
                    flex: 1;
                }
                
                .domain-icon {
                    width: clamp(45px, 10vw, 55px);
                    height: clamp(45px, 10vw, 55px);
                    border-radius: 14px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    color: white;
                    flex-shrink: 0;
                }
                
                .domain-details {
                    flex: 1;
                }
                
                .domain-details h3 {
                    color: white;
                    margin-bottom: 0.3rem;
                    font-size: clamp(1rem, 4vw, 1.2rem);
                }
                
                .domain-progress {
                    color: #aaa;
                    font-size: 0.85rem;
                }
                
                .progress-bar {
                    max-width: 300px;
                    width: 100%;
                    height: 8px;
                    border-radius: 20px;
                    background: rgba(255, 255, 255, 0.1);
                    margin-top: 0.5rem;
                    overflow: hidden;
                }
                
                .progress-fill {
                    height: 100%;
                    border-radius: 20px;
                    transition: width 0.5s ease;
                    position: relative;
                    overflow: hidden;
                }
                
                .progress-fill::after {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
                    animation: shimmer 2s infinite;
                }
                
                @keyframes shimmer {
                    0% {
                        transform: translateX(-100%);
                    }
                    100% {
                        transform: translateX(100%);
                    }
                }
                
                .motivation-message {
                    padding: 0.8rem 1rem;
                    margin: 0 1rem 1rem 1rem;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    gap: 0.8rem;
                    font-size: 0.85rem;
                    animation: fadeInUp 0.4s ease-out;
                    flex-wrap: wrap;
                }
                
                .success-toast {
                    position: fixed;
                    top: 20px;
                    right: 20px;
                    background: linear-gradient(135deg, #10b981, #34d399);
                    color: white;
                    padding: 1rem 1.5rem;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    gap: 0.8rem;
                    animation: slideIn 0.3s ease-out;
                    z-index: 1000;
                    box-shadow: 0 10px 25px rgba(0,0,0,0.2);
                }
                
                .topics-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
                    gap: 1rem;
                    padding: clamp(1rem, 3vw, 1.5rem);
                    border-top: 1px solid rgba(255, 255, 255, 0.1);
                }
                
                .topic-card {
                    background: rgba(45, 45, 51, 0.8);
                    backdrop-filter: blur(5px);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 16px;
                    padding: clamp(0.8rem, 3vw, 1rem);
                    display: flex;
                    align-items: center;
                    gap: clamp(0.8rem, 3vw, 1rem);
                    transition: all 0.3s ease;
                }
                
                .topic-card:hover {
                    transform: translateX(5px);
                    border-color: #4ade80;
                    background: rgba(45, 45, 51, 0.95);
                }
                
                .topic-icon {
                    width: 45px;
                    height: 45px;
                    border-radius: 12px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    background: rgba(90, 84, 235, 0.15);
                    color: #8b5cf6;
                    flex-shrink: 0;
                }
                
                .topic-info {
                    flex: 1;
                    min-width: 0;
                }
                
                .topic-info h4 {
                    color: white;
                    margin-bottom: 0.25rem;
                    font-size: 0.9rem;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                
                .topic-date {
                    color: #aaa;
                    font-size: 0.7rem;
                }
                
                .topic-actions {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    flex-shrink: 0;
                }
                
                .topic-download-btn {
                    border: none;
                    background: transparent;
                    color: #f59e0b;
                    cursor: pointer;
                    padding: 0.25rem;
                    transition: transform 0.2s ease;
                }
                
                .topic-download-btn:hover:not(:disabled) {
                    transform: scale(1.1);
                    color: #fbbf24;
                }
                
                .empty-state {
                    background: rgba(35, 35, 40, 0.8);
                    backdrop-filter: blur(10px);
                    border-radius: 20px;
                    padding: clamp(2rem, 10vw, 4rem) clamp(1rem, 5vw, 2rem);
                    text-align: center;
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    margin-top: 2rem;
                    animation: fadeInUp 0.6s ease-out;
                }
                
                .error-box {
                    background: rgba(59, 31, 31, 0.9);
                    backdrop-filter: blur(10px);
                    border: 1px solid #ef4444;
                    color: #fca5a5;
                    padding: 1rem;
                    border-radius: 14px;
                    margin-bottom: 2rem;
                    display: flex;
                    align-items: center;
                    gap: 0.7rem;
                    animation: fadeInUp 0.6s ease-out;
                }
                
                @media (max-width: 768px) {
                    .domain-info {
                        flex-wrap: wrap;
                    }
                    
                    .progress-bar {
                        max-width: 100%;
                    }
                    
                    .topic-info h4 {
                        white-space: normal;
                        word-break: break-word;
                    }
                    
                    .motivation-message {
                        flex-direction: column;
                        text-align: center;
                    }
                }
                
                @media (max-width: 480px) {
                    .certificate-actions {
                        flex-direction: column;
                    }
                    
                    .cert-btn {
                        width: 100%;
                        justify-content: center;
                    }
                }
            `}</style>

            {/* Success Toast Notification */}
            {showSuccess && (
                <div className="success-toast">
                    <Trophy size={24} />
                    <div>
                        <strong>🎉 Congratulations!</strong>
                        <p style={{ margin: 0, fontSize: "0.8rem" }}>
                            You've earned {showSuccess} Mastery Certificate!
                        </p>
                    </div>
                </div>
            )}

            {/* HEADER */}
            <div className="cert-header">
                <h1>🎓 My Certifications</h1>
                <p>Track your learning achievements and earn mastery certificates</p>
            </div>

            {/* ERROR */}
            {error && (
                <div className="error-box">
                    <AlertCircle size={20} />
                    {error}
                </div>
            )}

            {/* STATS */}
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-value">{totalCertificates}</div>
                    <div className="stat-label">Total Certificates</div>
                </div>
                <div className="stat-card">
                    <div className="stat-value">{domainCertificates.length}</div>
                    <div className="stat-label">Domain Mastery</div>
                </div>
                <div className="stat-card">
                    <div className="stat-value">{totalTopicsCompleted}</div>
                    <div className="stat-label">Topics Mastered</div>
                </div>
            </div>

            {/* DOMAIN CERTIFICATES FROM BACKEND */}
            {domainCertificates.length > 0 && (
                <>
                    <h2 className="section-title">
                        <Trophy size={24} color="#fbbf24" />
                        Domain Mastery Certificates
                        <span className="badge">{domainCertificates.length}</span>
                    </h2>
                    <div className="certificates-grid">
                        {domainCertificates.map((cert, index) => (
                            <div key={`domain-${index}`} className="certificate-card">
                                <div className="certificate-header" style={{ background: cert.bgGradient }}>
                                    {cert.icon}
                                </div>
                                <div className="certificate-body">
                                    <h3>{cert.name}</h3>
                                    <div className="certificate-date">Earned On: {cert.date}</div>
                                    <div className="certificate-name" style={{ background: `${cert.color}20`, color: cert.color }}>
                                        🏆 Mastery Certificate
                                    </div>
                                    <div className="certificate-actions">
                                        {cert.certificateUrl && (
                                            <>
                                                <button
                                                    className="cert-btn download-btn"
                                                    disabled={downloading}
                                                    onClick={() => handleDownloadCertificate(
                                                        cert.certificateUrl,
                                                        `${cert.name}_Mastery_Certificate.png`,
                                                        cert.certificateId
                                                    )}
                                                >
                                                    {downloading && downloadingId === cert.certificateId ? (
                                                        <Loader size={16} style={{ animation: "spin 1s linear infinite" }} />
                                                    ) : (
                                                        <Download size={16} />
                                                    )}
                                                    Download
                                                </button>
                                                <button
                                                    className="cert-btn view-btn"
                                                    onClick={() => window.open(cert.certificateUrl, "_blank")}
                                                >
                                                    <ExternalLink size={16} />
                                                    View
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* CERTIFICATES FROM BACKEND (Topic Certificates) */}
            {certificatesFromBackend.length > 0 && (
                <>
                    <h2 className="section-title">
                        <Award size={24} color="#f59e0b" />
                        Your Certificate Collection
                        <span className="badge">{certificatesFromBackend.length}</span>
                    </h2>
                    <div className="certificates-grid">
                        {certificatesFromBackend.map((cert) => (
                            <div key={cert.uniqueId || cert.certificateId} className="certificate-card">
                                <div className="certificate-header">
                                    <Star size={46} />
                                </div>
                                <div className="certificate-body">
                                    <h3>{cert.displayTitle || cert.quizTitle || "Certificate"}</h3>
                                    <div className="certificate-date">
                                        Issued: {safeDate(cert.issuedDate || cert.createdAt)}
                                    </div>
                                    <div className="certificate-name">
                                        Achievement Certificate
                                    </div>
                                    <div className="certificate-actions">
                                        <button
                                            className="cert-btn download-btn"
                                            disabled={downloading}
                                            onClick={() => handleDownloadCertificate(
                                                cert.certificateUrl,
                                                `${cert.displayTitle || cert.quizTitle || "certificate"}.png`,
                                                cert.certificateId
                                            )}
                                        >
                                            {downloading && downloadingId === cert.certificateId ? (
                                                <Loader size={16} style={{ animation: "spin 1s linear infinite" }} />
                                            ) : (
                                                <Download size={16} />
                                            )}
                                            Download
                                        </button>
                                        <button
                                            className="cert-btn view-btn"
                                            onClick={() => window.open(cert.certificateUrl, "_blank")}
                                        >
                                            <ExternalLink size={16} />
                                            View
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* DOMAIN PROGRESS WITH MOTIVATIONAL MESSAGES */}
            {Object.entries(domainGroups).some(([, domain]) => domain.completedTopics.length > 0) && (
                <>
                    <h2 className="section-title">
                        <TrendingUp size={24} color="#4ade80" />
                        Your Learning Journey
                    </h2>
                    {Object.entries(domainGroups).map(([domainName, domain]) => {
                        const motivation = getMotivationalMessage(domain);
                        const hasProgress = domain.completedTopics.length > 0;
                        const allCompleted = domain.completedTopics.length === domain.totalTopics;
                        
                        return (
                            <div key={domainName} className="domain-section">
                                <div className="domain-header" onClick={() => toggleDomain(domainName)}>
                                    <div className="domain-info">
                                        <div className="domain-icon" style={{ background: domain.bgGradient }}>
                                            {domain.icon}
                                        </div>
                                        <div className="domain-details">
                                            <h3>{domainName}</h3>
                                            <div className="domain-progress">
                                                {domain.completedTopics.length} / {domain.totalTopics} Topics Completed
                                            </div>
                                            <div className="progress-bar">
                                                <div
                                                    className="progress-fill"
                                                    style={{
                                                        width: `${(domain.completedTopics.length / domain.totalTopics) * 100}%`,
                                                        background: domain.bgGradient
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        {expandedDomains[domainName] ? <ChevronUp color="#fff" size={20} /> : <ChevronDown color="#fff" size={20} />}
                                    </div>
                                </div>
                                
                                {/* Motivational Message with Claim Button */}
                                {hasProgress && !domain.domainCertificateEarned && (
                                    <div className="motivation-message" style={{ background: domain.lightColor, borderLeft: `3px solid ${motivation.color}` }}>
                                        {motivation.icon}
                                        <span style={{ color: motivation.color, flex: 1 }}>{motivation.message}</span>
                                        {motivation.showProgress && (
                                            <span style={{ fontSize: "0.75rem", color: "#aaa" }}>
                                                {Math.round((domain.completedTopics.length / domain.totalTopics) * 100)}% Complete
                                            </span>
                                        )}
                                        {motivation.showClaimButton && allCompleted && (
                                            <button
                                                className="claim-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleClaimDomainCertificate(domainName);
                                                }}
                                                disabled={generatingDomain === domainName}
                                            >
                                                {generatingDomain === domainName ? (
                                                    <>
                                                        <Loader size={14} style={{ animation: "spin 1s linear infinite" }} />
                                                        Generating...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Gift size={14} />
                                                        Claim Certificate
                                                    </>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                )}
                                
                                {domain.domainCertificateEarned && (
                                    <div className="motivation-message" style={{ background: "rgba(251, 191, 36, 0.1)", borderLeft: "3px solid #fbbf24" }}>
                                        <Trophy size={20} color="#fbbf24" />
                                        <span style={{ color: "#fbbf24", flex: 1 }}>🎉 Amazing! You've mastered {domainName}! 🎉</span>
                                        {domain.domainCertificateUrl && (
                                            <button
                                                className="claim-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDownloadCertificate(
                                                        domain.domainCertificateUrl,
                                                        `${domainName}_Mastery_Certificate.png`,
                                                        domainName
                                                    );
                                                }}
                                                style={{ background: "#5a54eb", color: "white" }}
                                            >
                                                <Download size={14} />
                                                Download
                                            </button>
                                        )}
                                    </div>
                                )}
                                
                                {expandedDomains[domainName] && (
                                    <div className="topics-grid">
                                        {domain.completedTopics.map((topic, index) => (
                                            <div key={index} className="topic-card">
                                                <div className="topic-icon">{topic.icon}</div>
                                                <div className="topic-info">
                                                    <h4>{topic.name}</h4>
                                                    <div className="topic-date">Completed: {topic.date}</div>
                                                </div>
                                                <div className="topic-actions">
                                                    <CheckCircle color="#4ade80" size={18} />
                                                    {topic.hasRealCertificate && topic.certificateUrl && (
                                                        <button
                                                            className="topic-download-btn"
                                                            disabled={downloading}
                                                            onClick={() => handleDownloadCertificate(
                                                                topic.certificateUrl,
                                                                `${topic.name}.png`,
                                                                topic.certificateId
                                                            )}
                                                            title="Download Certificate"
                                                        >
                                                            {downloading && downloadingId === topic.certificateId ? (
                                                                <Loader size={16} style={{ animation: "spin 1s linear infinite" }} />
                                                            ) : (
                                                                <Download size={18} />
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </>
            )}

            {/* EMPTY STATE */}
            {certificatesFromBackend.length === 0 && domainCertificates.length === 0 && totalTopicsCompleted === 0 && (
                <div className="empty-state">
                    <Rocket size={70} color="#8b5cf6" />
                    <h2 style={{ color: "white", marginTop: "1rem", fontSize: "clamp(1.2rem, 5vw, 1.8rem)" }}>Start Your Learning Journey!</h2>
                    <p style={{ color: "#aaa", marginTop: "0.5rem" }}>
                        Complete quizzes and score above 60% to earn certificates
                    </p>
                    <button 
                        onClick={() => window.location.hash = "/quiz"}
                        style={{
                            marginTop: "1.5rem",
                            padding: "0.8rem 2rem",
                            background: "linear-gradient(135deg, #5a54eb, #8b5cf6)",
                            border: "none",
                            borderRadius: "12px",
                            color: "white",
                            fontWeight: "600",
                            cursor: "pointer",
                            transition: "transform 0.2s ease"
                        }}
                        onMouseEnter={(e) => e.target.style.transform = "translateY(-2px)"}
                        onMouseLeave={(e) => e.target.style.transform = "translateY(0)"}
                    >
                        Take a Quiz Now →
                    </button>
                </div>
            )}

            {/* MOTIVATION SECTION */}
            {totalTopicsCompleted > 0 && certificatesFromBackend.length === 0 && domainCertificates.length === 0 && (
                <div style={{
                    marginTop: "2rem",
                    background: "linear-gradient(135deg, rgba(90,84,235,0.15), rgba(139,92,246,0.08))",
                    padding: "clamp(1rem, 4vw, 1.5rem)",
                    borderRadius: "18px",
                    border: "1px solid #5a54eb",
                    textAlign: "center",
                    animation: "fadeInUp 0.6s ease-out"
                }}>
                    <Medal size={30} color="#fbbf24" style={{ marginBottom: "0.5rem" }} />
                    <h3 style={{ color: "white", marginTop: "0.5rem", fontSize: "clamp(1rem, 4vw, 1.2rem)" }}>Keep Going! 🚀</h3>
                    <p style={{ color: "#aaa", marginTop: "0.4rem" }}>
                        You've completed {totalTopicsCompleted} quiz{totalTopicsCompleted > 1 ? 'zes' : ''}! 
                        Score above 60% on each quiz to unlock your certificates.
                    </p>
                    <div style={{ 
                        marginTop: "1rem", 
                        display: "flex", 
                        gap: "1rem", 
                        justifyContent: "center",
                        flexWrap: "wrap"
                    }}>
                        {Object.entries(domainGroups).map(([domainName, domain]) => {
                            if (domain.completedTopics.length > 0 && !domain.domainCertificateEarned) {
                                const remaining = domain.totalTopics - domain.completedTopics.length;
                                const percentage = Math.round((domain.completedTopics.length / domain.totalTopics) * 100);
                                return (
                                    <div key={domainName} style={{ fontSize: "0.8rem", color: domain.color }}>
                                        {domainName}: {percentage}% Complete ({remaining} left)
                                    </div>
                                );
                            }
                            return null;
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CertificationPage;